import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ChatSidebar from 'components/chat/ChatSidebar';
import LoadingSpinner from 'components/atoms/LoadingSpinner';
import Button from 'components/atoms/Button';
import { useAlert } from 'context/AlertContext';
import { useDescope } from '@descope/react-sdk';
import { HistoryProvider } from 'context/HistoryContext';
import useAuthStore from 'stores/authStore';
import useSidebarStore from 'stores/sidebarStore';
import UserService from 'services/userService';
import { BackendUser, UserApiError, UserErrorType } from 'types/user.types';
import useChatStore from 'stores/chatStore';
import { analyticsService } from 'services/analyticsService';

const ProfileInner: React.FC = () => {
  const navigate = useNavigate();
  const { showError, showWarning, showInfo, showSuccess } = useAlert();
  const { getUserId, clearAuth } = useAuthStore();
  const { reset } = useChatStore();
  const { isCollapsed } = useSidebarStore();
  const sdk = useDescope();
  const [loading, setLoading] = useState<boolean>(true);
  const [userData, setUserData] = useState<BackendUser | null>(null);
  const [error, setError] = useState<{ type: UserErrorType; message: string } | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const userId = getUserId();

  const fetchUserData = useCallback(async (isRetry: boolean = false) => {
    if (!userId) {
      setError({
        type: UserErrorType.INVALID_DATA,
        message: 'No user ID found. Please log in again.',
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isRetry) {
        showInfo('Retrying...');
      }

      console.log('[Profile] Fetching user data for:', userId);
      const data = await UserService.getUserById(userId);
      setUserData(data);
      console.log('[Profile] User data loaded successfully');

      if (isRetry) {
        showInfo('Profile loaded successfully!');
      }
    } catch (err: any) {
      console.error('[Profile] Error fetching user data:', err);

      if (err instanceof UserApiError) {
        setError({ type: err.type, message: err.message });

        switch (err.type) {
          case UserErrorType.USER_NOT_FOUND:
            showError('Profile not found. Please try logging in again.');
            break;

          case UserErrorType.NETWORK_ERROR:
            showWarning('Network error. Check your connection and try again.');
            break;

          case UserErrorType.SERVER_ERROR:
            showError('Server error. Please try again in a few moments.');
            break;

          default:
            showError(err.message || 'Failed to load profile.');
        }
      } else {
        setError({
          type: UserErrorType.UNKNOWN_ERROR,
          message: 'An unexpected error occurred.',
        });
        showError('Failed to load profile data.');
      }
    } finally {
      setLoading(false);
    }
  }, [userId, showError, showWarning, showInfo]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleNewClassification = () => {
    reset();
    navigate('/');
  };

  const handleChangeName = () => {
    showInfo('Name change functionality coming soon!');
    // TODO: Implement name change modal/form
  };

  const handleChangeEmail = () => {
    showInfo('Email change functionality coming soon!');
    // TODO: Implement email change modal/form
  };

  const handleChangePassword = () => {
    showInfo('Password change functionality coming soon!');
    // TODO: Implement password change modal/form
  };

const handleLogout = async () => {
    try {
      // Track logout event BEFORE clearing auth (need userId)
      if (userId) {
        analyticsService.trackLogout(userId);
        console.log('✅ [Profile page] Logout event tracked in Mixpanel');
      } else {
        console.warn('⚠️ [Profile page] No userId available for logout tracking');
      }

      // Call Descope logout
      await sdk.logout();

      // Clear auth store (replaces localStorage clearing)
      clearAuth();

      // Show success message
      showSuccess('Successfully logged out');

      // Navigate to login page
      navigate('/login');

      // Reset Mixpanel session AFTER navigation (with delay to ensure events are sent)
      setTimeout(() => {
        analyticsService.reset();
      }, 500);
    } catch (error) {
      console.error('Logout error:', error);
      showError('Failed to logout. Please try again.');
    }
  };


  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (!userId) {
      showError('User ID not found. Please log in again.');
      return;
    }

    try {
      setIsDeleting(true);
      console.log('[Profile] Deleting user account:', userId);

      const result = await UserService.deleteUser(userId);

      console.log('[Profile] Account deleted successfully:', result.message);
      showSuccess('Your account has been deleted successfully.');

      // Clear auth and navigate to login after a short delay
      setTimeout(() => {
        clearAuth();
        navigate('/login');
      }, 1500);

    } catch (err: any) {
      console.error('[Profile] Error deleting account:', err);

      if (err instanceof UserApiError) {
        switch (err.type) {
          case UserErrorType.USER_NOT_FOUND:
            showError('Account not found. It may have already been deleted.');
            break;

          case UserErrorType.NETWORK_ERROR:
            showError('Network error. Please check your connection and try again.');
            break;

          case UserErrorType.SERVER_ERROR:
            showError('Server error. Please try again later.');
            break;

          default:
            showError(err.message || 'Failed to delete account. Please try again.');
        }
      } else {
        showError('An unexpected error occurred while deleting your account.');
      }
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="chat-page gradient-bg min-h-screen">
      <div className="gradient-content h-full">
        <ChatSidebar onNewClassification={handleNewClassification} />

        <div className={`flex-1 ${isCollapsed ? 'ml-[72px]' : 'ml-64'} transition-all duration-200`}>
          <div className="max-w-3xl mx-auto px-8 py-10">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <LoadingSpinner size="lg" />
              </div>
            ) : error ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-50 mb-4">
                    <svg className="h-8 w-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Profile</h3>
                  <p className="text-gray-500 mb-6">{error.message}</p>
                  <Button onClick={() => fetchUserData(true)} variant="teal">
                    Try Again
                  </Button>
                </div>
              </div>
            ) : userData ? (
              <div className="space-y-6">
                {/* Page Header */}
                <h1 className="text-2xl font-semibold text-gray-900">Account Settings</h1>

                {/* Profile Card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <div className="flex items-center gap-5">
                    {userData.picture ? (
                      <img
                        src={userData.picture}
                        alt={userData.name || 'User'}
                        className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center ring-2 ring-teal-100">
                        <span className="text-xl font-semibold text-white">
                          {userData.given_name?.[0]?.toUpperCase() || 'U'}
                          {userData.family_name?.[0]?.toUpperCase() || ''}
                        </span>
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        {[userData.given_name, userData.family_name].filter(Boolean).join(' ') || userData.name || 'User'}
                      </h2>
                      <p className="text-sm text-gray-500">{userData.email}</p>
                    </div>
                  </div>
                </div>

                {/* Personal Information Card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-5">Personal Information</h3>

                  <div className="grid grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1.5">
                        First Name
                      </label>
                      <input
                        type="text"
                        value={userData.given_name || ''}
                        readOnly
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1.5">
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={userData.family_name || ''}
                        readOnly
                        className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-4">
                    <Button
                      onClick={handleChangeName}
                      variant="outline"
                      className="text-xs"
                    >
                      Change Name
                    </Button>
                  </div>
                </div>

                {/* Security Card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-5">Security</h3>

                  {/* Email */}
                  <div className="mb-5">
                    <label className="block text-sm font-medium text-gray-500 mb-1.5">
                      Email
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="email"
                        value={userData.email}
                        readOnly
                        className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none"
                      />
                      <Button
                        onClick={handleChangeEmail}
                        variant="outline"
                        className="text-xs"
                      >
                        Change
                      </Button>
                    </div>
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1.5">
                      Password
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value="••••••••••"
                          readOnly
                          className="w-full px-4 py-2.5 pr-10 border border-gray-200 rounded-xl text-sm text-gray-900 bg-gray-50 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showPassword ? (
                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                            </svg>
                          ) : (
                            <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          )}
                        </button>
                      </div>
                      <Button
                        onClick={handleChangePassword}
                        variant="outline"
                        className="text-xs"
                      >
                        Change
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Danger Zone Card */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                  <h3 className="text-base font-semibold text-gray-900 mb-5">Account Actions</h3>

                  <div className="flex items-center justify-between py-3 border-b border-gray-100">
                    <div>
                      <p className="text-sm font-medium text-gray-900">Log out</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Sign out of your current session
                      </p>
                    </div>
                    <Button
                      onClick={handleLogout}
                      variant="outline"
                      className="text-xs"
                    >
                      Log out
                    </Button>
                  </div>

                  <div className="flex items-center justify-between py-3">
                    <div>
                      <p className="text-sm font-medium text-red-600">Delete account</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Permanently remove your account and all data
                      </p>
                    </div>
                    <Button
                      onClick={handleDeleteAccount}
                      variant="outline"
                      className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-12 text-center">
                <p className="text-gray-500">Unable to load profile data</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Account Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="mb-5">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-50 mb-4">
                <svg className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Delete Account
              </h3>
              <p className="text-sm text-gray-500 text-center">
                Are you sure you want to delete your account? This action cannot be undone. All your data, profile information, and chat history will be permanently removed.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleCancelDelete}
                variant="outline"
                className="flex-1 text-xs"
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmDelete}
                variant="outline"
                className="flex-1 text-xs bg-red-600 text-white border-red-600 hover:bg-red-700"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Account'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Profile: React.FC = () => {
  return (
    <HistoryProvider>
      <ProfileInner />
    </HistoryProvider>
  );
};

export default Profile;
